import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Shield, Target, Heart, Users } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="pt-24 pb-16" data-testid="about-page">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16 fade-in">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">About SafeShe Atlas</h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're on a mission to empower women travelers worldwide with accurate, real-time safety information that helps them explore the world confidently.
            </p>
          </div>

          {/* Mission Section */}
          <div className="bg-gradient-to-br from-purple-50 to-teal-50 rounded-3xl p-12 mb-16 fade-in">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-teal-500 flex items-center justify-center">
                  <Target className="w-12 h-12 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Mission</h2>
                <p className="text-base text-gray-700 leading-relaxed">
                  Every woman deserves to travel safely and confidently. SafeShe Atlas aggregates data from government sources, news outlets, and public reports to provide comprehensive, up-to-date safety rankings for tourist destinations worldwide. We believe that informed travelers are safer travelers.
                </p>
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Data-Driven</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Our Women Safety Index (WSI) is calculated using multiple verified data sources including crime statistics, emergency response ratings, and real-time incident reports.
              </p>
            </div>

            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Community-Focused</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                We monitor global news sources daily to capture emerging safety concerns and provide timely alerts to our subscribers.
              </p>
            </div>

            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Empathetic</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Beyond numbers, we provide actionable safety recommendations tailored to each destination's unique context and current conditions.
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16 fade-in">
            <h2 className="text-3xl font-bold mb-8 text-center">How SafeShe Atlas Works</h2>
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">Data Collection</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Every day at midnight UTC, our system fetches the latest safety data from government APIs, crime databases, and news sources for all tracked destinations.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">WSI Calculation</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Our algorithm analyzes crime rates (30%), emergency response quality (20%), public transport safety (20%), night safety (15%), and recent incidents (15%) to generate a composite Women Safety Index score from 0-100.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">Daily Alerts</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Subscribers receive personalized email alerts at 00:30 UTC with updated safety scores, trend analysis, and recent incidents for their selected destinations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Coverage */}
          <div className="text-center bg-gradient-to-br from-purple-600 to-teal-600 rounded-3xl p-12 text-white fade-in">
            <h2 className="text-3xl font-bold mb-6">Global Coverage</h2>
            <p className="text-base leading-relaxed max-w-2xl mx-auto mb-8">
              We currently track safety metrics for the world's top tourist destinations, with plans to expand coverage to more cities based on user demand.
            </p>
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-4xl font-bold mb-2">25+</div>
                <div className="text-sm opacity-90">Cities Tracked</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-90">Data Monitoring</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">Daily</div>
                <div className="text-sm opacity-90">Updates</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;